import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import { ContadorOperarService } from '../../../../../services/contador/contador-operar.service';
import { MoedaDTO } from '../../../../../models/configuracoes/empresa.model';

/**
 * Aba "Gestão de itens" da tela Operar Cliente (Área do Contador).
 *
 * Wrapper fino que reusa o componente COMPLETO de gestão de itens
 * (`app-lista-de-produtos`) em modo EMBUTIDO, sob o contexto do cliente
 * (`X-Empresa-Contexto` injetado pelo interceptor). Assim o contador consulta,
 * ADICIONA e EDITA os itens do cliente com o mesmo formulário da tela de
 * Artículos (variantes, modificadores, imagens etc.). Excluir e ajuste de
 * estoque ficam de fora (o backend só libera GET/POST/PUT de item sob contexto).
 *
 * O `lista-de-produtos` resolve a empresa (e carrega a lista) no `ngOnInit`, então
 * para SEMPRE refletir o cliente atual ele é RE-MONTADO:
 *   - ao ABRIR a aba (cada ativação carrega fresco sob o contexto vigente); e
 *   - ao TROCAR de cliente.
 * Isso evita a lista "presa" num carregamento antigo (ex.: montada antes de o
 * contexto do cliente estar ativo).
 *
 * MOEDA (por que este wrapper faz uma requisição própria): o `lista-de-produtos`
 * resolve a moeda por `GET /configuracaoEmpresa/sessao`, rota global (menu/header)
 * que fica fora do allowlist do contexto e ainda é cacheada por sessão — no
 * contexto do contador ela responde pela empresa do CONTADOR. O formulário
 * mascarava preço/custo na moeda errada (prefixo "Gs" e 0 casas decimais num
 * cliente em Dólar), gravando valores 100x maiores. Aqui a moeda vem de
 * `GET /configuracaoEmpresa/contador` (rota que honra o contexto) e é entregue
 * por `@Input` — mesmo padrão do fix do `CotacaoDialogComponent`.
 *
 * A montagem só ocorre DEPOIS de a moeda chegar: montar antes abriria uma janela
 * em que o contador poderia digitar um preço com a máscara errada.
 */
@Component({
  selector: 'app-operar-itens',
  templateUrl: './operar-itens.component.html',
  styleUrls: ['./operar-itens.component.scss'],
})
export class OperarItensComponent implements OnChanges, OnDestroy {
  /** Id da empresa do cliente — muda quando o contador troca de cliente. */
  @Input() clienteEmpresaId?: number | null;

  /** Indica se a aba está ativa; monta o componente pesado quando a aba abre. */
  @Input() ativo = false;

  /** Controla a existência do componente pesado (montagem sob demanda + re-mount). */
  montado = false;

  /** Moeda padrão da empresa do CLIENTE, repassada ao formulário de itens. */
  moedaCliente: MoedaDTO | null = null;

  carregando = false;
  erro = false;

  /** Requisição da moeda em voo — cancelada quando o contador troca de cliente. */
  private configSub?: Subscription;

  constructor(private operarService: ContadorOperarService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const abriuAgora =
      !!changes['ativo'] && this.ativo && changes['ativo'].previousValue !== true;
    const trocouCliente =
      !!changes['clienteEmpresaId'] && !changes['clienteEmpresaId'].firstChange;

    // Fechou a aba: desmonta (a próxima abertura carrega fresco sob o contexto atual).
    if (changes['ativo'] && !this.ativo) {
      this.cancelarConfigEmVoo();
      this.montado = false;
      this.carregando = false;
      this.erro = false;
      return;
    }

    // Abrir a aba (ou trocar de cliente com a aba aberta): resolve a moeda do
    // cliente e só então (re)monta o componente pesado, que carrega a lista e o
    // formulário sob o contexto vigente.
    if (abriuAgora || (trocouCliente && this.ativo)) {
      this.carregarMoedaEMontar();
    }
  }

  ngOnDestroy(): void {
    this.cancelarConfigEmVoo();
  }

  /** Ação do botão "tentar novamente" do estado de erro. */
  recarregar(): void {
    this.carregarMoedaEMontar();
  }

  /**
   * Busca a moeda da empresa do cliente e monta o formulário só depois.
   *
   * Desmonta ANTES de buscar: garante que a próxima montagem seja sempre uma
   * instância nova (novo `ngOnInit`, portanto nova carga sob o contexto vigente)
   * e que nenhum formulário fique visível com a moeda do cliente anterior.
   */
  private carregarMoedaEMontar(): void {
    this.cancelarConfigEmVoo();
    this.montado = false;
    this.moedaCliente = null;
    this.erro = false;
    this.carregando = true;

    this.configSub = this.operarService.buscarConfiguracao().subscribe({
      next: (config) => {
        this.moedaCliente = config?.moeda ?? null;
        this.carregando = false;
        // Recria o componente pesado já com a moeda correta em mãos.
        this.montado = true;
      },
      error: () => {
        // Inclui o 403 de cliente sem contrato ATIVO. Nunca montar o formulário
        // com moeda-palpite: um preço digitado com a máscara errada seria gravado
        // silenciosamente errado no catálogo do cliente.
        this.carregando = false;
        this.erro = true;
        this.montado = false;
      },
    });
  }

  private cancelarConfigEmVoo(): void {
    this.configSub?.unsubscribe();
    this.configSub = undefined;
  }
}
